/**
 * PDCA Step Card Component
 * Individual step card in the KATA Board
 */

import React, { useState } from 'react';
import { 
  CheckCircle, 
  Clock, 
  Play, 
  Pause, 
  AlertCircle,
  User,
  Calendar,
  Plus,
  MoreVertical,
  Target,
  Lightbulb,
  FileText,
  BarChart3
} from 'lucide-react';
import { PDCAStep } from '../../services/kataBoard';
import { kataUtils } from '../../services/kataBoard';
import { useKATABoardStore } from '../../stores/kataBoardStore';

interface PDCAStepCardProps {
  step: PDCAStep;
  onAddExperiment: () => void;
  onAddNote: () => void;
}

const PDCAStepCard: React.FC<PDCAStepCardProps> = ({ 
  step, 
  onAddExperiment, 
  onAddNote 
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const { updateStep, markStepCurrent } = useKATABoardStore();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Play className="h-4 w-4 text-blue-500" />;
      case 'blocked':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStepTypeIcon = (stepType: string) => {
    switch (stepType) {
      case 'plan':
        return <Target className="h-4 w-4" />;
      case 'do':
        return <Play className="h-4 w-4" />;
      case 'check':
        return <BarChart3 className="h-4 w-4" />;
      case 'act':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    await updateStep(step.id, { status: newStatus });
    setShowMenu(false);
  };

  const handleMarkCurrent = async () => {
    await markStepCurrent(step.id);
    setShowMenu(false);
  };

  return (
    <div className={`bg-white rounded-lg border-2 transition-all duration-200 ${
      step.is_current 
        ? 'border-blue-500 shadow-md' 
        : 'border-gray-200 hover:border-gray-300'
    }`}>
      {/* Card Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className={`p-1 rounded ${
              kataUtils.getStepTypeColor(step.step_type) === 'blue' ? 'bg-blue-100 text-blue-600' :
              kataUtils.getStepTypeColor(step.step_type) === 'green' ? 'bg-green-100 text-green-600' :
              kataUtils.getStepTypeColor(step.step_type) === 'orange' ? 'bg-orange-100 text-orange-600' :
              'bg-purple-100 text-purple-600'
            }`}>
              {getStepTypeIcon(step.step_type)}
            </div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {kataUtils.getStepTypeDisplay(step.step_type)}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {getStatusIcon(step.status)}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <MoreVertical className="h-4 w-4 text-gray-400" />
              </button>
              
              {showMenu && (
                <div className="absolute right-0 top-6 bg-white border rounded-lg shadow-lg z-10 min-w-[150px]">
                  <div className="py-1">
                    {!step.is_current && (
                      <button
                        onClick={handleMarkCurrent}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        Mark as Current
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleStatusChange('in_progress')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      Mark In Progress
                    </button>
                    
                    <button
                      onClick={() => handleStatusChange('completed')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      Mark Completed
                    </button>
                    
                    <button
                      onClick={() => handleStatusChange('blocked')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      Mark Blocked
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <h3 className="font-medium text-gray-900 mb-1">{step.title}</h3>
        
        {step.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{step.description}</p>
        )}

        {step.is_current && (
          <div className="mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full inline-block">
            Current Step
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 space-y-3">
        {/* Assigned To */}
        {step.assigned_to && (
          <div className="flex items-center text-sm text-gray-600">
            <User className="h-4 w-4 mr-2" />
            <span>{step.assigned_to.full_name}</span>
          </div>
        )}

        {/* Dates */}
        {(step.planned_start_date || step.actual_start_date) && (
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2" />
            <span>
              {step.actual_start_date 
                ? `Started ${new Date(step.actual_start_date).toLocaleDateString()}`
                : `Planned ${new Date(step.planned_start_date!).toLocaleDateString()}`
              }
            </span>
          </div>
        )}

        {/* Experiments */}
        {step.experiments.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Experiments ({step.experiments.length})
              </span>
              <button
                onClick={onAddExperiment}
                className="text-blue-600 hover:text-blue-800"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            
            <div className="space-y-1">
              {step.experiments.slice(0, 2).map((experiment) => (
                <div key={experiment.id} className="bg-gray-50 rounded p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {experiment.title}
                    </span>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      experiment.status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : experiment.status === 'running'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {kataUtils.getStatusDisplay(experiment.status)}
                    </div>
                  </div>
                  
                  {experiment.success !== null && (
                    <div className="mt-1 flex items-center">
                      {experiment.success ? (
                        <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-red-500 mr-1" />
                      )}
                      <span className="text-xs text-gray-600">
                        {experiment.success ? 'Successful' : 'Failed'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              
              {step.experiments.length > 2 && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showDetails ? 'Show Less' : `+${step.experiments.length - 2} more`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {step.notes.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Notes ({step.notes.length})
              </span>
              <button
                onClick={onAddNote}
                className="text-blue-600 hover:text-blue-800"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            
            <div className="space-y-1">
              {step.notes.slice(0, 2).map((note) => (
                <div key={note.id} className="bg-yellow-50 rounded p-2 border-l-2 border-yellow-400">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center">
                      {note.note_type === 'learning' && <Lightbulb className="h-3 w-3 text-yellow-600 mr-1" />}
                      {note.note_type === 'obstacle' && <AlertCircle className="h-3 w-3 text-red-600 mr-1" />}
                      {note.note_type === 'idea' && <Target className="h-3 w-3 text-blue-600 mr-1" />}
                      {note.note_type === 'observation' && <FileText className="h-3 w-3 text-gray-600 mr-1" />}
                      <span className="text-xs font-medium text-gray-700">
                        {kataUtils.getStatusDisplay(note.note_type)}
                      </span>
                    </div>
                    {note.is_important && (
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {note.title || note.content}
                  </p>
                </div>
              ))}
              
              {step.notes.length > 2 && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showDetails ? 'Show Less' : `+${step.notes.length - 2} more`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {step.results && (
          <div className="bg-green-50 rounded p-3 border-l-2 border-green-400">
            <h4 className="text-sm font-medium text-green-900 mb-1">Results</h4>
            <p className="text-sm text-green-800">{step.results}</p>
          </div>
        )}

        {/* Next Actions */}
        {step.next_actions && (
          <div className="bg-blue-50 rounded p-3 border-l-2 border-blue-400">
            <h4 className="text-sm font-medium text-blue-900 mb-1">Next Actions</h4>
            <p className="text-sm text-blue-800">{step.next_actions}</p>
          </div>
        )}
      </div>

      {/* Card Actions */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              onClick={onAddExperiment}
              className="text-xs bg-white border border-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-50 flex items-center"
            >
              <BarChart3 className="h-3 w-3 mr-1" />
              Experiment
            </button>
            
            <button
              onClick={onAddNote}
              className="text-xs bg-white border border-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-50 flex items-center"
            >
              <FileText className="h-3 w-3 mr-1" />
              Note
            </button>
          </div>
          
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {showDetails ? 'Less' : 'More'}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {showDetails && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-4">
            {/* All Experiments */}
            {step.experiments.length > 2 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">All Experiments</h4>
                <div className="space-y-2">
                  {step.experiments.slice(2).map((experiment) => (
                    <div key={experiment.id} className="bg-white rounded p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{experiment.title}</span>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          experiment.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : experiment.status === 'running'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {kataUtils.getStatusDisplay(experiment.status)}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{experiment.hypothesis}</p>
                      
                      {experiment.actual_results && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Results: </span>
                          <span className="text-gray-600">{experiment.actual_results}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Notes */}
            {step.notes.length > 2 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">All Notes</h4>
                <div className="space-y-2">
                  {step.notes.slice(2).map((note) => (
                    <div key={note.id} className="bg-white rounded p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          {note.note_type === 'learning' && <Lightbulb className="h-4 w-4 text-yellow-600 mr-2" />}
                          {note.note_type === 'obstacle' && <AlertCircle className="h-4 w-4 text-red-600 mr-2" />}
                          {note.note_type === 'idea' && <Target className="h-4 w-4 text-blue-600 mr-2" />}
                          {note.note_type === 'observation' && <FileText className="h-4 w-4 text-gray-600 mr-2" />}
                          <span className="font-medium text-gray-900">
                            {note.title || kataUtils.getStatusDisplay(note.note_type)}
                          </span>
                        </div>
                        {note.is_important && (
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600">{note.content}</p>
                      
                      <div className="mt-2 text-xs text-gray-500">
                        by {note.author.full_name} • {new Date(note.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PDCAStepCard;
